import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Skeleton, styled } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { RequestType } from "@interfaces";
import {
  Body,
  ContentContainer,
  FullScreenLoader,
  Header,
  MainActionButton,
  NavigateBeforeButton,
  SearchBar,
} from "@components";
import { Card, PatientCard } from "./components/Card";

import { useDoctorContext } from "@contexts";
import { useCreateRequest, useDoctorPatients } from "@hooks";
import type { ClinicalHistoryPatient } from "@hooks/clinicalHistory/useDoctorPatients";
import Fuse from "fuse.js";
import { format, validate } from "rut.js";
import { useClearAllInteractionsStores } from "@hooks/useInteractionsStore";
import { redirectToEmptyDocumentFirstStep } from "@utils/redirect";

const Search = styled(SearchBar)({
  position: "static",
  maxWidth: "100%",
});

const PatientsWrapper = styled(Body)({
  gap: "16px",
});

const PatientCardsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  borderRadius: "4px",
  backgroundColor: theme.palette.background.paper,
}));

const EmptyRequestWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "screenCentered",
})<{ screenCentered?: boolean }>(({ screenCentered, theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  paddingTop: screenCentered ? "72px" : "8px",
  color: theme.palette.text.secondary,
}));

const EmptyRequestContainer = styled(EmptyRequestWrapper)({
  gap: "8px",
  padding: "0",
});

const StyledInfoIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: 24,
  color: theme.palette.icon.primary,
}));

const SkeletonsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  marginTop: "16px",
  width: "100%",
});

interface NewDocumentPatientProps {
  documentType: RequestType;
}

export function NewDocumentPatient() {
  const navigate = useNavigate();
  const doctor = useDoctorContext();

  const location = useLocation();
  const { documentType = RequestType.PRESCRIPTION }: NewDocumentPatientProps =
    location.state || {};
  const { data: patients, isPending: isPendingPatients } = useDoctorPatients(
    doctor.code,
  );
  const { mutate: createRequest, isPending: isCreatingRequest } =
    useCreateRequest();
  const [search, setSearch] = useState("");
  useClearAllInteractionsStores();

  const allPatients = useMemo(() => patients ?? [], [patients]);

  const fusePatients = useMemo(
    () =>
      new Fuse(
        allPatients.map((patient) => {
          const isRut = validate(patient.documentNumber);
          return {
            ...patient,
            fRut: isRut
              ? format(patient.documentNumber)
              : patient.documentNumber,
            cleanRut: patient.documentNumber.replace(/[.-]/g, ""),
          };
        }),
        {
          keys: [
            "name",
            "firstSurname",
            "secondSurname",
            "fRut",
            "cleanRut",
            "documentNumber",
          ],
          threshold: 0.1,
        },
      ),
    [allPatients],
  );

  const filteredPatients = useMemo(
    () =>
      search.length > 0
        ? fusePatients.search(search).map((result) => result.item)
        : allPatients,
    [search, fusePatients, allPatients],
  );

  const handleAddPatient = useCallback(() => {
    navigate("/app/nuevo-documento/paciente/agregar", {
      state: {
        documentType,
        ...(/^[\d.-]+$/.test(search) && { searchedRut: search }),
      },
    });
  }, [navigate, documentType, search]);

  const handlePatientCardClick = useCallback(
    (patient: ClinicalHistoryPatient) => {
      if (!patient.patientUuid) {
        console.error("Patient uuid is required");
        return;
      }

      createRequest(
        {
          patientUuid: patient.patientUuid,
          documentType: documentType,
        },
        {
          onSuccess: (request) => {
            const { id: requestId, documentType: docType } = request;
            navigate(redirectToEmptyDocumentFirstStep(docType, requestId));
          },
          onError: (error) => {
            console.log("Error creating request: ", error);
            navigate("/app/solicitudes");
          },
        },
      );
    },
    [documentType, createRequest, navigate],
  );

  if (isCreatingRequest) {
    return (
      <>
        <Header
          title="Paciente"
          titleColor="black"
          left={
            <NavigateBeforeButton
              onClick={() => navigate("/app/nuevo-documento")}
            />
          }
        />
        <ContentContainer flex={1} disableGutters>
          <Body justifyContent="center">
            <FullScreenLoader title="Cargando..." />
          </Body>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <Header
        title="Paciente"
        titleColor="black"
        left={
          <NavigateBeforeButton
            onClick={() => navigate("/app/nuevo-documento")}
          />
        }
      />
      <ContentContainer disableGutters>
        <PatientsWrapper>
          <MainActionButton
            variant="outlined"
            size="large"
            onClick={handleAddPatient}
            disabled={isPendingPatients}
          >
            Agregar paciente
          </MainActionButton>

          {!isPendingPatients &&
            (allPatients.length > 0 ? (
              <>
                <Search
                  placeholder="Buscar por nombre o RUT"
                  searchText={search}
                  setSearchText={setSearch}
                  obscureInTracker
                />
                {filteredPatients.length > 0 ? (
                  <PatientCardsContainer>
                    {filteredPatients.map((patient) => {
                      const isRut = validate(patient.documentNumber);
                      return (
                        <PatientCard
                          key={patient.documentNumber ?? patient.patientUuid}
                          name={patient.name}
                          lastName={`${patient.firstSurname} ${
                            patient.secondSurname || ""
                          }`}
                          rut={isRut ? patient.documentNumber : undefined}
                          passport={!isRut ? patient.documentNumber : undefined}
                          onClick={() => handlePatientCardClick(patient)}
                        />
                      );
                    })}
                  </PatientCardsContainer>
                ) : (
                  <EmptyRequestWrapper>
                    <EmptyRequestContainer>
                      <StyledInfoIcon />
                      No se encontraron resultados
                    </EmptyRequestContainer>
                  </EmptyRequestWrapper>
                )}
              </>
            ) : (
              <EmptyRequestWrapper screenCentered>
                <EmptyRequestContainer>
                  <StyledInfoIcon />
                  No has agregado pacientes
                </EmptyRequestContainer>
              </EmptyRequestWrapper>
            ))}
          {isPendingPatients && <NewDocumentPatientSkeleton />}
        </PatientsWrapper>
      </ContentContainer>
    </>
  );
}

function NewDocumentPatientSkeleton() {
  return (
    <SkeletonsContainer>
      <Skeleton variant="rectangular" height={48} />
      <PatientCardsContainer>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card.Skeleton key={index} />
        ))}
      </PatientCardsContainer>
    </SkeletonsContainer>
  );
}
